import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtNum = (n) => {
  if (n == null) return "—";
  const v = Number(n);
  if (Number.isNaN(v)) return n;
  return v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const fmtPct = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `${(Number(n) * 100).toFixed(2)}%`;
};

const ClientGoogleAnalytics = ({ clientId }) => {
  const [status, setStatus] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const [savingProp, setSavingProp] = useState(false);
  const [error, setError] = useState(null);

  const [report, setReport] = useState({ overview: null, traffic: null, funnel: null, channel: null });
  const [loadingReport, setLoadingReport] = useState(false);

  const [startDate, setStartDate] = useState("30daysAgo");
  const [endDate, setEndDate] = useState("today");

  const loadStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/google-analytics/clients/${clientId}/status`);
      setStatus(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load status");
    }
  }, [clientId]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Auto-detect ?google_analytics=connected param after OAuth callback
  useEffect(() => {
    const u = new URL(window.location.href);
    if (u.searchParams.get("google_analytics") === "connected") {
      loadStatus();
      // clean the param so refresh doesn't keep showing the toast
      u.searchParams.delete("google_analytics");
      window.history.replaceState({}, "", u.toString());
    }
    const err = u.searchParams.get("google_analytics_error");
    if (err) {
      setError(`OAuth error: ${err}`);
      u.searchParams.delete("google_analytics_error");
      window.history.replaceState({}, "", u.toString());
    }
  }, [loadStatus]);

  const connect = async () => {
    setError(null);
    try {
      const res = await axios.get(`${API}/google-analytics/auth-url`, { params: { client_id: clientId } });
      const url = res.data?.url;
      if (url) window.location.href = url;
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to start OAuth");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Analytics for this client?")) return;
    try {
      await axios.post(`${API}/google-analytics/clients/${clientId}/disconnect`);
      setStatus({ connected: false });
      setProperties([]);
      setReport({ overview: null, traffic: null, funnel: null, channel: null });
    } catch (e) {
      setError(e.response?.data?.detail || "Disconnect failed");
    }
  };

  const loadProperties = async () => {
    setLoadingProps(true); setError(null);
    try {
      const res = await axios.get(`${API}/google-analytics/clients/${clientId}/properties`);
      setProperties(res.data?.properties || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to list properties");
    } finally {
      setLoadingProps(false);
    }
  };

  const selectProperty = async (property_id, property_name) => {
    setSavingProp(true); setError(null);
    try {
      await axios.post(`${API}/google-analytics/clients/${clientId}/property`, { property_id, property_name });
      await loadStatus();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save property");
    } finally {
      setSavingProp(false);
    }
  };

  const loadReports = async () => {
    setLoadingReport(true); setError(null);
    try {
      const params = { start_date: startDate, end_date: endDate };
      const [ov, traffic, funnel, channel] = await Promise.all([
        axios.get(`${API}/google-analytics/clients/${clientId}/overview`, { params }),
        axios.get(`${API}/google-analytics/clients/${clientId}/traffic-sources`, { params }),
        axios.get(`${API}/google-analytics/clients/${clientId}/funnel`, { params }),
        axios.get(`${API}/google-analytics/clients/${clientId}/revenue-by-channel`, { params }),
      ]);
      setReport({
        overview: ov.data,
        traffic: traffic.data?.report,
        funnel: funnel.data,
        channel: channel.data?.report,
      });
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load GA reports");
    } finally {
      setLoadingReport(false);
    }
  };

  // Auto-load reports when property is set
  useEffect(() => {
    if (status?.connected && status?.selected_property_id) {
      loadReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.selected_property_id]);

  const connected = !!status?.connected;
  const hasProperty = !!status?.selected_property_id;

  return (
    <div className="md:col-span-2 p-4 bg-[#1A1A1A] border border-[#2A2A2A] rounded space-y-3" data-testid="client-ga-section">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-white flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full" style={{ background: connected ? "#22C55E" : "#71717A" }} />
            Google Analytics 4
          </h3>
          <p className="text-xs text-[#71717A] mt-0.5">
            {connected
              ? `Connected${status?.email ? ` as ${status.email}` : ""}${hasProperty ? ` · Property: ${status.selected_property_name || status.selected_property_id}` : " · No property selected"}`
              : "Connect the client's Google Analytics account to see traffic, funnel & channel revenue."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!connected && (
            <button
              onClick={connect}
              className="px-3 py-1.5 text-xs rounded font-medium"
              style={{ background: "#E5683E", color: "#fff" }}
              data-testid="ga-connect-btn"
            >
              Connect Google Analytics
            </button>
          )}
          {connected && (
            <>
              <button
                onClick={connect}
                className="px-2 py-1 text-xs rounded border border-[#3A3A3A] text-[#A1A1AA] hover:text-white"
                data-testid="ga-reconnect-btn"
              >
                Reconnect
              </button>
              <button
                onClick={disconnect}
                className="px-2 py-1 text-xs rounded border border-[#3A3A3A] text-[#A1A1AA] hover:text-white"
                data-testid="ga-disconnect-btn"
              >
                Disconnect
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="text-xs p-2 rounded" style={{ background: "#FF444422", color: "#FF6666" }} data-testid="ga-error">
          {error}
        </div>
      )}

      {connected && (
        <div className="space-y-3 pt-2 border-t border-[#2A2A2A]">
          {/* Property picker */}
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={loadProperties}
              disabled={loadingProps}
              className="px-3 py-1.5 text-xs rounded border border-[#3A3A3A] text-[#A1A1AA] hover:text-white"
              data-testid="ga-load-properties-btn"
            >
              {loadingProps ? "Loading…" : (properties.length ? "Refresh properties" : "Load GA4 properties")}
            </button>
            {properties.length > 0 && (
              <select
                className="input-default text-xs py-1 px-2 w-72"
                value={status?.selected_property_id || ""}
                onChange={(e) => {
                  const p = properties.find(x => x.property_id === e.target.value);
                  if (p) selectProperty(p.property_id, p.property_name);
                }}
                disabled={savingProp}
                data-testid="ga-property-select"
              >
                <option value="">Select GA4 property…</option>
                {properties.map(p => (
                  <option key={p.property_id} value={p.property_id}>
                    {p.property_name} ({p.account_name})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Date range */}
          {hasProperty && (
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-xs text-[#71717A]">Range:</label>
              <select
                className="input-default text-xs py-1 px-2"
                value={startDate === "7daysAgo" ? "7d" : startDate === "90daysAgo" ? "90d" : "30d"}
                onChange={(e) => {
                  const v = e.target.value;
                  setStartDate(v === "7d" ? "7daysAgo" : v === "90d" ? "90daysAgo" : "30daysAgo");
                  setEndDate("today");
                }}
                data-testid="ga-range-select"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
              <button
                onClick={loadReports}
                disabled={loadingReport}
                className="px-3 py-1.5 text-xs rounded font-medium"
                style={{ background: loadingReport ? "#52525B" : "#1877F2", color: "#fff" }}
                data-testid="ga-refresh-reports-btn"
              >
                {loadingReport ? "Loading…" : "Refresh reports"}
              </button>
            </div>
          )}

          {/* Reports */}
          {hasProperty && report.overview && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2" data-testid="ga-kpi-grid">
              {[
                ["Sessions", report.overview.kpis?.sessions],
                ["Active Users", report.overview.kpis?.activeUsers],
                ["Page Views", report.overview.kpis?.screenPageViews],
                ["New Users", report.overview.kpis?.newUsers],
                ["Bounce Rate", fmtPct(report.overview.kpis?.bounceRate)],
                ["Engagement Rate", fmtPct(report.overview.kpis?.engagementRate)],
                ["Transactions", report.overview.kpis?.transactions],
                ["GA4 Revenue", report.overview.kpis?.totalRevenue],
              ].map(([label, val]) => (
                <div key={label} className="p-2 rounded bg-[#0F0F0F] border border-[#2A2A2A]">
                  <div className="text-[10px] uppercase text-[#71717A]">{label}</div>
                  <div className="text-sm font-mono text-white">{typeof val === "string" ? val : fmtNum(val)}</div>
                </div>
              ))}
            </div>
          )}

          {hasProperty && report.funnel && (
            <div className="p-3 rounded bg-[#0F0F0F] border border-[#2A2A2A]" data-testid="ga-funnel">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-white">Funnel: view_item → add_to_cart → begin_checkout → purchase</div>
                <div className="text-xs text-[#71717A]">View → Purchase: <span className="text-white font-mono">{report.funnel.view_to_purchase_pct}%</span></div>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {(report.funnel.steps || []).map((s) => (
                  <div key={s.event} className="p-2 rounded bg-[#1A1A1A] border border-[#2A2A2A]">
                    <div className="text-[10px] uppercase text-[#71717A]">{s.event}</div>
                    <div className="text-sm font-mono text-white">{fmtNum(s.users)} <span className="text-[10px] text-[#71717A]">users</span></div>
                    <div className="text-[10px] text-[#71717A]">{fmtNum(s.event_count)} events</div>
                    {s.drop_off_pct_from_prev != null && (
                      <div className="text-[10px]" style={{ color: s.drop_off_pct_from_prev > 50 ? "#FF6666" : "#86EFAC" }}>
                        -{s.drop_off_pct_from_prev}% drop
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasProperty && report.channel && (
            <div className="p-3 rounded bg-[#0F0F0F] border border-[#2A2A2A]" data-testid="ga-revenue-channel">
              <div className="text-xs font-semibold text-white mb-2">Revenue by Channel (GA4)</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[#71717A]">
                    <tr><th className="text-left py-1">Channel</th><th className="text-right">Sessions</th><th className="text-right">Users</th><th className="text-right">Transactions</th><th className="text-right">Revenue</th></tr>
                  </thead>
                  <tbody>
                    {(report.channel.rows || []).slice(0, 10).map((r, i) => (
                      <tr key={i} className="border-t border-[#2A2A2A]">
                        <td className="py-1 text-white">{r.sessionDefaultChannelGroup || "(unassigned)"}</td>
                        <td className="text-right font-mono">{fmtNum(r.sessions)}</td>
                        <td className="text-right font-mono">{fmtNum(r.totalUsers)}</td>
                        <td className="text-right font-mono">{fmtNum(r.transactions)}</td>
                        <td className="text-right font-mono text-white">{fmtNum(r.totalRevenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {hasProperty && report.traffic && (
            <div className="p-3 rounded bg-[#0F0F0F] border border-[#2A2A2A]" data-testid="ga-traffic-sources">
              <div className="text-xs font-semibold text-white mb-2">Top Traffic Sources</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[#71717A]">
                    <tr><th className="text-left py-1">Source</th><th className="text-left">Medium</th><th className="text-right">Sessions</th><th className="text-right">Users</th><th className="text-right">New Users</th><th className="text-right">Bounce</th></tr>
                  </thead>
                  <tbody>
                    {(report.traffic.rows || []).slice(0, 15).map((r, i) => (
                      <tr key={i} className="border-t border-[#2A2A2A]">
                        <td className="py-1 text-white">{r.sessionSource}</td>
                        <td className="text-[#A1A1AA]">{r.sessionMedium}</td>
                        <td className="text-right font-mono">{fmtNum(r.sessions)}</td>
                        <td className="text-right font-mono">{fmtNum(r.activeUsers)}</td>
                        <td className="text-right font-mono">{fmtNum(r.newUsers)}</td>
                        <td className="text-right font-mono">{fmtPct(r.bounceRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClientGoogleAnalytics;
